'use client'

import { Button } from '@/core/components/form/button/button'
import { LuShieldAlert } from 'react-icons/lu'

import SafeImage from '@/features/veluvit/components/ui/media-image'
import { cn } from '@/core/utils/common'

import { ItemArt } from '@/features/veluvit/components/ui/item-art'
import { useAppData } from '@/features/veluvit/hooks/use-app-data'
import { openUnlock } from '@/features/veluvit/services/collection.actions'

import type { GridItem } from '@/features/veluvit/lib/rules'

/**
 * 컬렉션 장면 카드 한 장 (레퍼런스 cc()) — 열리면 그림 그대로, 잠기면 흐린 그림 위에 자물쇠 그림 하나.
 * 2026-10-07 — 등급 색으로 칠하던 자물쇠 · 동그라미 테두리 · 열린 카드 밑 등급 색 띠를 걷었다(카드마다 색이 달라 어수선했다).
 * 자물쇠는 모든 잠긴 장면에 같은 그림(ItemArt 'lock' — 진주빛 하트 자물쇠). 등급 · 해금 조건은 누르면 뜨는 해금 창이 말한다.
 * 성인 컷은 인증 전까지 더 강하게 흐리고 방패 표시.
 */
export default function CollectionCard({
  workId,
  item,
  small,
  bare,
}: {
  workId: string
  item: GridItem
  small?: boolean
  /** 장소 묶음 안(work-collection)처럼 장소가 이미 머리에 있을 때 — 왼쪽 위 "장소 #번호" 꼬리표를 뺀다 */
  bare?: boolean
}) {
  const { ownership, thumb } = useAppData()
  const unlocked = ownership.unlocked.has(`${workId}:${item.i}`)
  const adultHidden = item.adult
  return (
    <Button
      type="button"
      onClick={() => openUnlock(workId, item.i)}
      aria-label={`${item.place} #${item.no} ${unlocked && !adultHidden ? '열린 장면' : '잠긴 장면'}`}
      // block w-full — 격자 칸이 아닌 가로 줄(work-collection) 안에서도 폭을 채운다. 없으면 폭 0 이라 그림이 통째로 안 보였다
      className="relative block aspect-[3/4] w-full cursor-pointer overflow-hidden rounded-field bg-black"
      variant="bare"
    >
      {unlocked && !adultHidden ? (
        <SafeImage
          src={item.img}
          fallbackSrc={thumb(workId)}
          alt=""
          fill
          sizes="(min-width: 1001px) 170px, 120px"
          className="object-cover object-[50%_30%]"
          appearance="plain"
        />
      ) : (
        <span
          aria-hidden="true"
          className="absolute inset-0 bg-[linear-gradient(135deg,var(--color-raised),var(--color-background))]"
        />
      )}
      {!small && !bare && (
        <span className="absolute top-1.25 left-1.25 rounded-control bg-black/45 px-1.25 py-px text-c9h text-t2 backdrop-blur-md">
          {item.place} #{item.no}
        </span>
      )}
      {adultHidden ? (
        <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-c10h font-bold text-warn [text-shadow:0_1px_3px_rgb(0_0_0/0.7)]">
          <LuShieldAlert aria-hidden="true" className="size-5" />
          {!small && '인증 후 열람'}
        </span>
      ) : (
        !unlocked && (
          <span className="absolute inset-0 grid place-items-center">
            <ItemArt
              name="lock"
              px={128}
              className={cn(
                'drop-shadow-[0_4px_10px_rgb(0_0_0/0.55)]',
                small ? 'size-7' : 'size-9'
              )}
            />
          </span>
        )
      )}
    </Button>
  )
}
